import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { isEmpty, omitBy, isNil } from "lodash";
import { useHistory, useLocation } from "react-router-dom";
import { encode, routeToEnrouteView } from "Helpers/index";
import moment from "moment";
import DataTableFilter from "Components/shared/data-table-filter";
import { DataTable } from "@life-line-lab/components-react";
import { BasicModal, Button } from "@life-line-lab/components-react";
import PlanNewTrainModal from "Components/delivery-management/parcel-tracking-modals/new-train-modal/plan-new-train-modal";
import { useAccessControlList, useSession } from "Hooks/index";
import { toCapitalisedSpaced } from "Helpers/to-capitalised-spaced";
import { getStatusStyle } from "Helpers/get-status-colours";

interface EnrouteTrainsListProps {
  trainLineOptions?: any;
  onFilter?: any;
  trainStationOptions?: any;
  trainList?: any;
  getTrainStations?: any;
  onSubmitNewTrainPlan?: any;
}

const EnrouteTrainsList: React.FC<EnrouteTrainsListProps> = ({
  onFilter,
  trainLineOptions,
  trainList,
  onSubmitNewTrainPlan,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const initialModal: any = {
    open: false,
  };
  const [modal, setModal] = useState<any>(initialModal);
  const [callModal, setCallModal] = useState<any>(<></>);
  const handleResetModalState = () => {
    setModal(initialModal);
  };

  const { roleTypes } = useSession();
  const permission = useAccessControlList({
    roleTypes: roleTypes,
    resource: "Delivery_Management",
  });

  const canCreate = permission.rights.includes("CREATE");

  const initialFilter = {
    trainStatus: "",
    trainLine: "",
    fromDate: "",
    toDate: moment(),
  };

  const [filterState, setFilterState] = useState<any>(initialFilter);

  const initialFilterSelection = [
    {
      type: "date",
      key: "fromDate",
      name: "fromDate",
      placeholder: "YYYY-MM-DD",
      label: t("label.from"),
      value: filterState.fromDate ? moment(filterState.fromDate) : undefined,
    },
    {
      type: "date",
      key: "toDate",
      name: "toDate",
      placeholder: "YYYY-MM-DD",
      label: t("label.to"),
      value: filterState.toDate ? moment(filterState.toDate) : moment(),
    },
    {
      key: "trainStatus",
      name: "trainStatus",
      placeholder: t("placeholder.byStatus"),
      label: t("placeholder.byStatus"),
      value: filterState?.trainStatus,
      options: [
        { label: t("option.all"), value: "" },
        { label: "Planned", value: "PLANNED" },
        { label: "Completed", value: "COMPLETED" },
        { label: "Ongoing", value: "ONGOING" },
      ],
    },
    {
      key: "trainLine",
      name: "trainLine",
      placeholder: t("placeholder.filterByTrainLine"),
      label: t("placeholder.filterByTrainLine"),
      value: filterState?.trainLine,
      options: [{ label: t("option.all"), value: "" }].concat(
        trainLineOptions.map((train) => {
          return {
            label: train.name,
            value: train._id,
          };
        }),
      ),
    },
  ];

  const [filterSelection, setFilterSelection] = useState<any>(
    initialFilterSelection,
  )

  const handleSearch = async (text: string) => {
    setFilterState((prevFilter) => ({ ...prevFilter, orFields: text }));
  };

  const handleFilterSelection = (selection) => {
    setFilterState((prevFilter) => ({
      ...prevFilter,
      fromDate: selection.fromDate,
      toDate: selection.toDate,
      trainStatus: selection.trainStatus,
      trainLine: selection.trainLine,
    }));
  };

  const handleOpenCompanyEnrouteView = (enrouteID) => {
    routeToEnrouteView(enrouteID, location, history)
  };

  const handleOpenNewTrainModal = () => {
    setCallModal(
      <PlanNewTrainModal
        trainLineOptions={trainLineOptions}
        onSubmit={(value) => {
          onSubmitNewTrainPlan(value);
          setModal({ open: false });
        }}
        onCancel={handleResetModalState}
      />,
    );
    setModal({ open: true });
  };

  const columns: any = [
    {
      title: t("label.date").toLocaleUpperCase(),
      dataIndex: "createdAt",
      width: 70,
      ellipsis: true,
      sorter: (a, b) =>
        a.createdAt > b.createdAt ? 1 : a.createdAt < b.createdAt ? -1 : 0,
      defaultSortOrder: "descend",
      render: (createdAt: string) => {
        return createdAt ? (
          <span>
            {moment(createdAt).format("DD-MM-YYYY") +
              " | " +
              moment(createdAt).format("hh:mm a")}
          </span>
        ) : (
          <span>&#11835;</span>
        );
      },
    },
    {
      title: t("label.journeyNumber").toLocaleUpperCase(),
      dataIndex: "journeyCode",
      width: 70,
      ellipsis: true,
      render: (journeyCode: string) => {
        return <a className="clickable-id">{journeyCode}</a>;
      },
      onCell: (record: any) => {
        return {
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            handleOpenCompanyEnrouteView(record._id);
          },
        };
      },
    },
    {
      title: t("label.status").toLocaleUpperCase(),
      dataIndex: "status",
      width: 100,
      ellipsis: true,
      render: (text: any) => {
        return (
          <span className="status-indicator" style={getStatusStyle(text)}>
            {text ? toCapitalisedSpaced(text) : ""}
          </span>
        );
      },
    },
    {
      title: t("label.trainLine").toLocaleUpperCase(),
      dataIndex: ["trainLineData", "name"],
      width: 100,
      ellipsis: true,
    },
    {
      title: t("label.trainPlate").toLocaleUpperCase(),
      dataIndex: "plateNo",
      width: 100,
      ellipsis: true,
      render: (plateNo: string) => {
        return plateNo ?? <span>&#11835;</span>;
      },
    },
    {
      title: t("label.startStation").toLocaleUpperCase(),
      dataIndex: "",
      width: 100,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          record?.trainLineData &&
          record?.trainLineData?.stations?.map(
            (trainStop) =>
              trainStop?.stationCode === record?.startStation &&
              trainStop?.stationName,
          )
        );
      },
    },
    {
      title: t("label.lastStation").toLocaleUpperCase(),
      dataIndex: "",
      width: 100,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          record?.trainLineData &&
          record?.trainLineData?.stations?.map(
            (trainStop) =>
              trainStop?.stationCode === record?.lastStation &&
              trainStop?.stationName,
          )
        );
      },
    },
    {
      title: t("label.plannedDeparture").toLocaleUpperCase(),
      dataIndex: "plannedDeparture",
      width: 100,
      ellipsis: true,
      render: (plannedDeparture: string) => {
        return plannedDeparture ? (
          <span>
            {moment(plannedDeparture).format("DD-MM-YYYY") +
              " | " +
              moment(plannedDeparture).format("hh:mm a")}
          </span>
        ) : (
          <span>&#11835;</span>
        );
      },
    },
    {
      title: t("label.actualDeparture").toLocaleUpperCase(),
      dataIndex: "actualDeparture",
      width: 100,
      ellipsis: true,
      render: (actualDeparture: string) => {
        return actualDeparture ? (
          <span>
            {moment(actualDeparture).format("DD-MM-YYYY") +
              " | " +
              moment(actualDeparture).format("hh:mm a")}
          </span>
        ) : (
          <span>&#11835;</span>
        );
      },
    },
    {
      title: t("label.lastLocation").toLocaleUpperCase(),
      dataIndex: ["lastKnownLocationData", "locationName"],
      width: 100,
      ellipsis: true,
    },
  ];

  useEffect(() => {
    setFilterSelection && setFilterSelection(initialFilterSelection);
  }, [trainLineOptions]);

  useEffect(() => {
    if (onFilter && !isEmpty(filterState)) {
      const filterPayload = omitBy(
        filterState,
        (input) => isNil(input) || isEmpty(input),
      );
      onFilter(filterPayload);
    }
  }, [filterState]);

  return (
    <div className="parcel-order-list">
      <DataTable
        rowKey="_id"
        columns={columns}
        scroll={{ x: true }}
        dataSource={trainList}
        title={() => (
          <div>
            <div className="filter-component">
              <DataTableFilter
                searchPlaceholder={t("placeholder.enrouteTrainSearchbar")}
                onSearch={(text: string) => {
                  handleSearch(text);
                }}
                onFilter={(selection) => handleFilterSelection(selection)}
                renderFilterSelection={filterSelection}
              />
              {canCreate && (
                <div className="mt-10">
                  <Button
                    className="mr-5"
                    type="primary"
                    label={t("action.planNewTrain")}
                    size="large"
                    onClick={handleOpenNewTrainModal}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      />

      <BasicModal
        className="modal"
        isOpen={modal.open}
        isClosable={true}
        isMaskClosable={false}
        onClose={() => {
          handleResetModalState();
        }}
      >
        {callModal}
      </BasicModal>
    </div>
  );
};

export default EnrouteTrainsList;
